/**
 * Kiosk Store - Shared reactive state management
 * Simplified store for wrestler-first check-in flow
 */

export function useKioskStore() {
  const state = {
    debug: false,

    // Auth info
    kiosk: null,

    // Team info (branding, name, USAW requirements, etc)
    teamInfo: null,

    // Barcode scanning
    scan: '',
    lookingUpId: false,

    // Current flow state (3-step wizard)
    selectedWrestler: null, // Step 1: Wrestler selected
    selectedEvent: null, // Step 2: Event selected

    // Data
    allWrestlers: [], // Cache of all wrestlers for search
    todaysEvents: [], // All events happening today
    eventsLoadedDate: null, // Date string (YYYY-MM-DD) when events were loaded
    recentCheckIns: [], // Recent check-ins to display

    // Custom questions
    checkInQuestions: [],
    pendingCheckIn: null, // Check-in awaiting question answers
    editingCheckIn: null, // Currently editing check-in
    checkInsToFinalize: [], // Queue of check-ins needing questions

    // Warnings
    wrestlerWarnings: [], // USAW, paused sub, roster mismatch

    // Loading states
    loadingCheckIns: false,

    // Settings
    forceUsawOff: false,
  };

  function setTeamInfo(team) {
    if (state.debug) console.log('setTeamInfo triggered with', team);
    state.teamInfo = team;
  }

  function setTodaysEvents(events) {
    if (state.debug) console.log('setTodaysEvents triggered with', events);
    state.todaysEvents = events;
  }

  function setAllWrestlers(wrestlers) {
    if (state.debug) console.log('setAllWrestlers triggered with', wrestlers);
    state.allWrestlers = wrestlers;
  }

  function selectWrestler(wrestler) {
    if (state.debug) console.log('selectWrestler triggered with', wrestler);
    state.selectedWrestler = wrestler;
    state.selectedEvent = null; // Reset event when new wrestler selected
    state.wrestlerWarnings = [];
  }

  function selectEvent(event) {
    if (state.debug) console.log('selectEvent triggered with', event);
    state.selectedEvent = event;
  }

  function resetFlow() {
    if (state.debug) console.log('resetFlow triggered');
    state.selectedWrestler = null;
    state.selectedEvent = null;
    state.wrestlerWarnings = [];
  }

  function addToCheckIns(checkIn) {
    if (state.debug) console.log('pushing check in', checkIn);
    const existingIdx = _.findIndex(state.recentCheckIns, (ch) => ch.id === checkIn.id);
    if (existingIdx !== -1) {
      // We already had this check in, move it to the front of the array
      state.recentCheckIns.unshift(state.recentCheckIns.splice(existingIdx, 1)[0]);
    } else {
      // Just add the checkin
      state.recentCheckIns.unshift(checkIn);
    }
    // If we have custom questions needed to finalize a check in, push it onto the queue
    if (state.checkInQuestions.length > 0) {
      setCheckInToFinalize(checkIn);
    }
  }

  function setCheckIns(checkIns) {
    if (state.debug) console.log('setCheckIns triggered with', checkIns);
    state.recentCheckIns = checkIns;
  }

  function setCheckInQuestions(questions) {
    if (state.debug) console.log('setCheckInQuestions triggered with', questions);
    state.checkInQuestions = questions;
  }

  function setCheckInToFinalize(checkIn) {
    if (state.debug) console.log('setCheckInToFinalize triggered with', checkIn);
    state.checkInsToFinalize.push(checkIn);
    // If we aren't busy, dequeue the next check in.
    if (!state.editingCheckIn) {
      dequeueNextCheckInForFinalization();
    }
  }

  function dequeueNextCheckInForFinalization() {
    if (state.debug) console.log('dequeueNextCheckInForFinalization triggered');
    const nextCheckIn = _.head(state.checkInsToFinalize);
    setEditingCheckIn(nextCheckIn);
  }

  function setEditingCheckIn(checkIn) {
    if (state.debug) console.log('setEditingCheckIn triggered with', checkIn);
    state.editingCheckIn = checkIn;
  }

  function checkInFinalized(checkInId) {
    if (state.debug) console.log('checkInFinalized triggered with', checkInId);
    _.remove(state.checkInsToFinalize, (ch) => ch.id === checkInId);
    if (checkInId === state.editingCheckIn?.id) {
      dequeueNextCheckInForFinalization();
    }
  }

  // Multi-club storage. `wiq_kiosks` holds every kiosk saved on this device
  // (same shape as `wiq_kiosk`); `wiq_kiosk` stays a mirror of the active one
  // so legacy boot code and rollbacks keep working.
  function readSavedKiosks() {
    const saved = JSON.parse(localStorage.getItem('wiq_kiosks'));
    return Array.isArray(saved) ? saved : null;
  }

  function writeSavedKiosks(kiosks) {
    localStorage.setItem('wiq_kiosks', JSON.stringify(kiosks));
  }

  function getSavedKiosks() {
    try {
      return readSavedKiosks() || [];
    } catch (e) {
      return [];
    }
  }

  // Merges into the existing entry so fields only returned by kiosk_sessions#create
  // (e.g. admin_pin) survive a refresh. Returns the merged kiosk.
  function upsertSavedKiosk(kiosk) {
    const kiosks = getSavedKiosks();
    const idx = _.findIndex(kiosks, (k) => k.id === kiosk.id);
    let merged = kiosk;
    if (idx === -1) {
      kiosks.push(kiosk);
    } else {
      const existing = kiosks[idx];
      merged = {
        ...existing,
        ...kiosk,
        team: { ...(existing.team || {}), ...(kiosk.team || {}) },
      };
      kiosks.splice(idx, 1, merged);
    }
    writeSavedKiosks(kiosks);
    return merged;
  }

  function removeSavedKiosk(id) {
    const kiosks = getSavedKiosks().filter((k) => k.id !== id);
    writeSavedKiosks(kiosks);
    if (localStorage.getItem('wiq_active_kiosk_id') === String(id)) {
      localStorage.removeItem('wiq_active_kiosk_id');
    }
    // Drop the mirror too, or loadActiveKiosk would re-adopt the removed kiosk
    // if the caller's follow-up setActiveKiosk never lands
    const legacy = parseLegacyKiosk();
    if (legacy && legacy.id === id) {
      localStorage.removeItem('wiq_kiosk');
    }
    return kiosks;
  }

  function setActiveKiosk(kiosk) {
    const merged = upsertSavedKiosk(kiosk);
    localStorage.setItem('wiq_active_kiosk_id', String(merged.id));
    setKiosk(merged);
    return merged;
  }

  function clearActiveKiosk() {
    localStorage.removeItem('wiq_active_kiosk_id');
    setKiosk(null);
  }

  function parseLegacyKiosk() {
    try {
      const legacy = JSON.parse(localStorage.getItem('wiq_kiosk'));
      return legacy && legacy.id && legacy.team && legacy.team.api_key ? legacy : null;
    } catch (e) {
      return null;
    }
  }

  // Reads saved kiosks (migrating a legacy single `wiq_kiosk` on first run) and
  // returns the kiosk that should boot, or null if the device needs setup.
  // Does not touch state or the auth header - callers refresh from the server first.
  function loadActiveKiosk() {
    const legacy = parseLegacyKiosk();
    let kiosks;
    try {
      kiosks = readSavedKiosks();
    } catch (e) {
      // Corrupt list - rebuild from the mirror if we can
      kiosks = legacy ? [legacy] : [];
      writeSavedKiosks(kiosks);
    }

    if (kiosks === null) {
      // First run on a pre-multi-club device: seed from the legacy single kiosk and
      // carry its device-wide settings over to that club. Runs exactly once, and the
      // legacy settings key is left in place for rollbacks.
      kiosks = legacy ? [legacy] : [];
      writeSavedKiosks(kiosks);
      if (legacy) {
        const legacySettings = localStorage.getItem('wiq_kiosk_settings');
        if (legacySettings && !localStorage.getItem(settingsKey(legacy.id))) {
          localStorage.setItem(settingsKey(legacy.id), legacySettings);
        }
        localStorage.setItem('wiq_active_kiosk_id', String(legacy.id));
      }
    }

    // Very old kiosk data (pre-March 2021) is missing an id and can't be refreshed
    const valid = kiosks.filter((k) => k && k.id && k.team && k.team.api_key);
    if (valid.length !== kiosks.length) {
      kiosks = valid;
      writeSavedKiosks(kiosks);
    }

    // The mirror can point at a kiosk the list doesn't know about (e.g. signed in
    // during a rollback) - adopt it since it's what the device last used
    if (legacy && !_.some(kiosks, (k) => k.id === legacy.id)) {
      kiosks.push(legacy);
      writeSavedKiosks(kiosks);
      localStorage.setItem('wiq_active_kiosk_id', String(legacy.id));
    }

    if (kiosks.length === 0) {
      localStorage.removeItem('wiq_kiosk');
      localStorage.removeItem('wiq_active_kiosk_id');
      return null;
    }

    const activeId = localStorage.getItem('wiq_active_kiosk_id');
    return _.find(kiosks, (k) => String(k.id) === activeId)
      || (legacy && _.find(kiosks, (k) => k.id === legacy.id))
      || kiosks[0];
  }

  // Kiosk settings (USAW/AAU requirements, warp speed, ...) are stored per club
  function settingsKey(kioskId = state.kiosk?.id) {
    return `wiq_kiosk_settings:${kioskId}`;
  }

  function setKiosk(kiosk) {
    if (kiosk) {
      localStorage.setItem('wiq_kiosk', JSON.stringify(kiosk));
      axios.defaults.headers.common.Authorization = `Bearer ${kiosk.team.api_key}`;
      state.kiosk = kiosk;
    } else {
      axios.defaults.headers.common.Authorization = 'Bearer';
      localStorage.removeItem('wiq_kiosk');
      state.kiosk = null;
    }
  }

  return {
    state,
    setTeamInfo,
    setTodaysEvents,
    setAllWrestlers,
    selectWrestler,
    selectEvent,
    resetFlow,
    addToCheckIns,
    setCheckIns,
    setCheckInQuestions,
    setCheckInToFinalize,
    dequeueNextCheckInForFinalization,
    setEditingCheckIn,
    checkInFinalized,
    setKiosk,
    getSavedKiosks,
    upsertSavedKiosk,
    removeSavedKiosk,
    setActiveKiosk,
    clearActiveKiosk,
    loadActiveKiosk,
    settingsKey,
  };
}
